import React, { createContext, useState, useEffect, useContext } from 'react';
import { User, Session, AuthError } from '@supabase/supabase-js';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { initializeUserSystems } from '@/utils/initializeUserSystems';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signIn: (email: string, password: string, captchaToken?: string) => Promise<void>;
  signUp: (email: string, password: string, name: string, captchaToken?: string) => Promise<void>;
  signOut: () => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signInWithFacebook: () => Promise<void>;
  resetPassword: (email: string, captchaToken?: string) => Promise<void>;
  refreshUser: () => Promise<void>;
  isNewUser: boolean;
  setIsNewUser: (value: boolean) => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [isNewUser, setIsNewUser] = useState(false);

  useEffect(() => {
    // Set up auth state listener first
    // Skip TOKEN_REFRESHED and MFA events — they don't change user identity
    // and cause unnecessary re-renders / DB re-fetches on tab focus
    const meaningfulEvents = new Set(['SIGNED_IN', 'SIGNED_OUT', 'USER_UPDATED', 'PASSWORD_RECOVERY', 'INITIAL_SESSION']);

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, currentSession) => {
        if (!meaningfulEvents.has(event)) return;

        console.log('Auth state changed:', event, 'Session:', !!currentSession, 'User:', !!currentSession?.user);
        setSession(currentSession);
        setUser(currentSession?.user ?? null);
        setLoading(false);

        if (event === 'SIGNED_IN') {
          console.log('User signed in, checking profile...');
          // Check if user profile exists, if not create it
          if (currentSession?.user) {
            createUserProfileIfNotExists(currentSession.user);
            // Initialize presence/status tracking on sign in
            initializeUserSystems().catch((e) => console.error('Failed to initialize user systems:', e));
          }
        }
      }
    );

    // Then check for existing session
    supabase.auth.getSession().then(({ data: { session: currentSession } }) => {
      setSession(currentSession);
      setUser(currentSession?.user ?? null);
      setLoading(false);
      if (currentSession?.user) {
        // Initialize presence/status tracking for existing session
        initializeUserSystems().catch((e) => console.error('Failed to initialize user systems:', e));
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const createUserProfileIfNotExists = async (user: User) => {
    try {
      console.log('Checking profile for user:', user.id);
      
      // Check if profile exists
      const { data: existingProfile, error: fetchError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      if (fetchError && fetchError.code !== 'PGRST116') { // PGRST116 is "no rows returned"
        console.error('Error fetching profile:', fetchError);
        throw fetchError;
      }

      // If no profile exists, create one and redirect to complete profile
      if (!existingProfile) {
        console.log('No profile found, creating new profile for user:', user.id);
        setIsNewUser(true);
        
        const { error } = await supabase.from('profiles').insert({
          id: user.id,
          email: user.email,
          full_name: user.user_metadata?.full_name || user.user_metadata?.name || '',
          avatar_url: user.user_metadata?.avatar_url || '',
          created_at: new Date().toISOString(),
        });

        if (error) {
          console.error('Error creating profile:', error);
          throw error;
        }
        
        console.log('Profile created successfully, user will be directed to complete profile on next navigation');
      } else {
        console.log('Existing profile found for user:', user.id, existingProfile);
        
        // Check if profile is incomplete (missing username or other required fields)
        const isProfileIncomplete = !existingProfile.username || 
                                   existingProfile.username.trim() === '' ||
                                   !existingProfile.full_name ||
                                   existingProfile.full_name.trim() === '';
        
        if (isProfileIncomplete) {
          console.log('Profile is incomplete, setting as new user');
          setIsNewUser(true);
        } else {
          console.log('Profile is complete, no additional setup needed');
          setIsNewUser(false);
        }
      }
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      console.error('Error creating user profile:', errorMessage);
    }
  };

  const signIn = async (email: string, password: string, captchaToken?: string) => {
    try {
      setLoading(true);
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
        options: captchaToken ? { captchaToken } : undefined,
      });
      
      if (error) throw error;
      
      // Don't redirect here - let the SignIn component handle the redirect
      // based on the auth state change and proper React Router navigation
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? getErrorMessage(error) : "An error occurred during sign in.";
      toast.error(errorMessage);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const signUp = async (email: string, password: string, name: string, captchaToken?: string) => {
    try {
      setLoading(true);
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            full_name: name,
          },
          // Include CAPTCHA token if provided
          ...(captchaToken ? { captchaToken } : {}),
        },
      });
      
      if (error) {
        // Check for specific error messages that indicate email already exists
        if (error.message.includes('User already registered') || 
            error.message.includes('already been registered') ||
            error.message.includes('email address is already registered')) {
          throw new Error("Email already present in our record, please login");
        }
        throw error;
      }
      
      // Check if user was created but email already exists (alternative detection method)
      if (data.user && data.user.identities && data.user.identities.length === 0) {
        throw new Error("Email already present in our record, please login");
      }
      
      // Check if user is immediately signed in (no email confirmation required)
      if (data.session && data.user) {
        // User is immediately signed in - set as new user and redirect will happen in auth state change
        setIsNewUser(true);
        toast.success("Account created successfully! Setting up your profile...");
      } else if (data.user && !data.session) {
        // User created but needs email confirmation
        // For localhost/dev, sometimes email confirmation is disabled or auto-confirmed
        // We should check if we can sign in immediately
        try {
            const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({
                email,
                password
            });
            
            if (!signInError && signInData.session) {
                setIsNewUser(true);
                toast.success("Account created and signed in!");
            } else {
                toast.success("Account created successfully! Please check your email to confirm your account before signing in.");
            }
        } catch (e) {
            toast.success("Account created successfully! Please check your email to confirm your account before signing in.");
        }
      } else {
        // Fallback success message
        toast.success("Account created successfully! Welcome to the platform.");
      }
      
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? getErrorMessage(error) : "An error occurred during sign up.";
      toast.error(errorMessage);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const signOut = async () => {
    try {
      await supabase.auth.signOut();
      window.location.href = '/signin';
    } catch (error: unknown) {
      const errorMessage = getErrorMessage(error);
      toast.error(errorMessage);
    }
  };

  const signInWithGoogle = async () => {
    try {
      console.log('Starting Google sign in...');
      // Get current URL to use as redirect URL
      const currentUrl = window.location.origin;
      console.log('Current origin:', currentUrl);
      
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: currentUrl,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent',
          }
        },
      });
      
      console.log('OAuth response:', data);
      
      if (error) {
        console.error('OAuth error:', error);
        throw error;
      }
    } catch (error: unknown) {
      console.error('Google sign in failed:', error);
      const errorMessage = getErrorMessage(error);
      toast.error(errorMessage);
      throw error;
    }
  };

  const signInWithFacebook = async () => {
    try {
      // Get current URL to use as redirect URL
      const currentUrl = window.location.origin;
      
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'facebook',
        options: {
          redirectTo: currentUrl,
        },
      });
      
      if (error) throw error;
    } catch (error: unknown) {
      const errorMessage = getErrorMessage(error);
      toast.error(errorMessage);
      throw error;
    }
  };

  const resetPassword = async (email: string, captchaToken?: string) => {
    try {
      setLoading(true);
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
        captchaToken,
      });
      
      if (error) throw error;
      
      return;
    } catch (error: unknown) {
      const errorMessage = getErrorMessage(error);
      toast.error(errorMessage);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const refreshUser = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase.auth.getUser();
      if (error) {
        throw error;
      }
      
      if (data && data.user) {
        setUser(data.user);
        console.log('User data refreshed in AuthContext:', data.user);
      }
    } catch (error: unknown) {
      if (error instanceof Error) {
        console.error('Failed to refresh user:', error.message);
      } else {
        console.error('Failed to refresh user:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  const getErrorMessage = (error: unknown): string => {
    const defaultMessage = "An error occurred during authentication. Please try again.";
    
    if (error && typeof error === 'object' && 'message' in error) {
      const msg = String((error as AuthError | Error).message);
      switch (msg) {
        case "Email not confirmed":
          return "Please check your email to confirm your account before signing in.";
        case "Invalid login credentials":
          return "Invalid email or password. Please try again.";
        case "User already registered":
          return "This email is already registered. Please try signing in instead.";
        case "Email already present in our record, please login":
          return "Email already present in our record, please login";
        default:
          return msg || defaultMessage;
      }
    }
    return defaultMessage;
  };

  return (
    <AuthContext.Provider value={{
      user,
      session,
      loading,
      signIn,
      signUp,
      signOut,
      signInWithGoogle,
      signInWithFacebook,
      resetPassword,
      refreshUser,
      isNewUser,
      setIsNewUser
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
